import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BankTransactionRecord,
  Buyer,
  DocumentsSummary,
  QuickbooksInvoice,
  SupplierForBuyer,
} from '@dill/dill-shared';
import {
  canUserAccessBuyerBranch,
  ERP_POSTING_NOTE_MAX_LENGTH,
  isDepositOpenForLinking,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { createManualRemittanceThunk } from '../../../../redux/services/cashApplicationService';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../redux/services/suppliersService';
import { getGroupedSuppliersInvoices } from '../../../../redux/services/invoicesService';
import { DillModal, DillButton, DillInput, DillField, DillSelect } from '../../../general/Dill';
import { AllocationEditor } from '../components/AllocationEditor';
import { File, Paperclip, X } from 'phosphor-react';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import { formatCurrency, formatDate } from '../utils';

/**
 * Recording a payment nobody sent paperwork for.
 *
 * Opened from two places with the same shape. From Awaiting Bank it records advice that arrived
 * outside every ingestion channel — a phone call, a customer portal, a note on a statement.
 * From Unmatched Deposits it describes money already in the feed, and that deposit arrives
 * pre-selected and locked, because a record created for a specific deposit and then left
 * unlinked is just another orphan.
 *
 * The operator picks the customer and the invoices, so nothing here is guessed: an explicit
 * allocation is written at full confidence rather than being put back through fuzzy matching,
 * which could only disagree with a decision already made on screen.
 */

interface AddRemittanceModalProps {
  open: boolean;
  onClose: () => void;
  /** When set the deposit is pre-selected and locked, and the record settles against it. */
  deposit?: BankTransactionRecord | null;
}

interface Attachment {
  url: string;
  name: string;
  fileType: string;
}

const todayIso = () => new Date().toISOString().split('T')[0];

const describeDeposit = (deposit: BankTransactionRecord): string =>
  deposit.merchantName || deposit.name || 'Bank deposit';

export const AddRemittanceModal = ({ open, onClose, deposit = null }: AddRemittanceModalProps) => {
  const dispatch = useAppDispatch();
  const bankTransactions = useAppSelector((state) => state.cashApplication.bankTransactions);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const user = useAppSelector((state) => state.auth.user);
  const { customers } = useAppSelector((state) => state.supplier);
  const supplierId = currentSupplier?.id;

  // Tracks the active supplier id for in-flight customer fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);

  const [customerId, setCustomerId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [checkNumber, setCheckNumber] = useState('');
  const [memo, setMemo] = useState('');
  const [postingNote, setPostingNote] = useState('');
  const [depositId, setDepositId] = useState('');
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customerCount, setCustomerCount] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [buyerInvoices, setBuyerInvoices] = useState<QuickbooksInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // A branch-scoped user can only record payments for customers in their branches — the same
  // rule the payment-request flow applies, and for the same reason: offering a customer whose
  // documents that user cannot see invites an allocation they cannot check.
  const selectableCustomers = useMemo(
    () =>
      customers.filter((customerDetails) =>
        canUserAccessBuyerBranch({
          user,
          supplierId: currentSupplier?.id,
          buyerBranch: customerDetails?.buyer?.branch,
        })
      ),
    [customers, user, currentSupplier]
  );

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
        isInvitedUser: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    fetchSupplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!fetchSupplierId || activeFetchSupplierIdRef.current !== fetchSupplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId: fetchSupplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers = (payload.data ?? []) as unknown[];
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            break;
          }
        }
      } else {
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoadingCustomers(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    try {
      const request = dispatch(
        getUserCustomers({
          supplierId: fetchSupplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        return;
      }
      if (results.type === 'supplier/getUserCustomers/fulfilled' && results.payload) {
        const payload = results.payload as any;
        const cust = (payload.data ?? []) as {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
            isInvitedUser: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[];
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);
          const count = Number(payload.customerCount);
          // Awaited so the dropdown keeps showing the loading indicator until every page
          // of customers has streamed in, not just the first one.
          await recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
        }
      }
    } finally {
      // A newer fetch (e.g. after switching suppliers) owns the loading state now.
      if (activeFetchSupplierIdRef.current === fetchSupplierId) {
        setLoadingCustomers(false);
      }
    }
  };

  /**
   * A customer's invoices, fetched when they are chosen rather than up front.
   *
   * The modal used to derive both the customer list and their invoices from every open invoice
   * the supplier has, which meant opening it pulled the whole book — for a supplier with
   * thousands of invoices that is a slow call to make before the operator has typed anything,
   * and all but one customer's worth of it is discarded.
   */
  const fetchBuyerInvoices = async (buyerId: string) => {
    if (!buyerId || !currentSupplier?.id) {
      setBuyerInvoices([]);
      return;
    }
    setLoadingInvoices(true);
    try {
      const results = await dispatch(
        getGroupedSuppliersInvoices({
          buyerId,
          weeksLimit: 12,
          supplierId: currentSupplier?.id || '',
        })
      );
      if (results && results.type === 'suppliers/getGroupedSuppliersInvoices/fulfilled') {
        const payload = results.payload as any;
        if (payload?.data?.buyerSuppliers) {
          const data = payload.data.buyerSuppliers as SupplierForBuyer[];
          let invoices: QuickbooksInvoice[] = [];
          data.forEach((item) => {
            invoices = [...invoices, ...(item.invoices?.pastDueInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.currentInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.pendingPayInvoices.invoices || [])];
          });
          // Only what still owes something. Paid invoices and credit memos are in the response
          // too, and neither is somewhere a customer's cheque can be applied.
          setBuyerInvoices(invoices.filter((invoice) => (invoice.balance ?? 0) > 0));
          return;
        }
      }
      setBuyerInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Reset on each open. A deposit's amount and date are facts, so they seed the form and stay
  // locked rather than being offered as defaults to retype.
  useEffect(() => {
    if (!open) {
      return;
    }
    setCustomerId('');
    setPayerName('');
    setAmount(deposit ? String(deposit.amount) : '');
    setPaymentDate(deposit ? deposit.date : todayIso());
    setCheckNumber('');
    setMemo('');
    setPostingNote('');
    setDepositId(deposit ? deposit.id : '');
    setAllocations({});
    setInvoiceSearch('');
    setAttachment(null);
    setBuyerInvoices([]);
    handleLoadMore({ isFreshFetch: true });
  }, [open, deposit]);

  const customerOptions = useMemo(
    () =>
      selectableCustomers
        .map((customerDetails) => ({
          value: customerDetails.buyer.id,
          label: customerDetails.buyer.name || customerDetails.buyer.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectableCustomers]
  );

  // Only this customer's invoices can be allocated — mixing customers on one payment is a
  // different operation, and picking one by accident is easy when the list is unfiltered.
  const customerInvoices = useMemo<QuickbooksInvoice[]>(() => {
    if (!customerId) {
      return [];
    }
    const term = invoiceSearch.trim().toLowerCase();
    return buyerInvoices.filter(
      (invoice) => !term || (invoice.docNumber ?? '').toLowerCase().includes(term)
    );
  }, [buyerInvoices, customerId, invoiceSearch]);

  /** Deposits that could still settle something, newest first. */
  const linkableDeposits = useMemo(
    () =>
      bankTransactions
        .filter((record) => record.bankStatus === 'POSTED' && isDepositOpenForLinking(record))
        .sort((a, b) => b.date.localeCompare(a.date)),
    [bankTransactions]
  );

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;

  const totalAllocated = useMemo(
    () => Math.round(Object.values(allocations).reduce((sum, value) => sum + value, 0) * 100) / 100,
    [allocations]
  );

  const overAllocated = amountValid && totalAllocated > parsedAmount + 0.005;
  /**
   * `uploading` blocks the save, because the attachment is only attached if it finished.
   *
   * `attachment` is set by the upload's continuation, so saving mid-upload sends `sourceFile: null`
   * and the record is written with no document — silently, since the payment itself saves fine. The
   * loss shows up much later as a QuickBooks payment with nothing attached to it. The file input is
   * already disabled while uploading; the button that discards the result was not.
   */
  const canSave =
    payerName.trim().length > 0 && amountValid && !overAllocated && !saving && !uploading;

  const handleCustomerChange = useCallback(
    (value: string) => {
      setCustomerId(value);
      // Allocations belong to the customer that was selected when they were made.
      setAllocations({});
      setInvoiceSearch('');
      setBuyerInvoices([]);
      const match = customerOptions.find((customer) => customer.value === value);
      setPayerName(match?.label ?? '');
      fetchBuyerInvoices(value);
    },
    [customerOptions, currentSupplier?.id]
  );

  const handleAllocationChange = useCallback((invoiceId: string, value: string) => {
    const parsed = Number(value);
    setAllocations((previous) => {
      const next = { ...previous };
      if (!value || !Number.isFinite(parsed) || parsed <= 0) {
        delete next[invoiceId];
      } else {
        next[invoiceId] = Math.round(parsed * 100) / 100;
      }
      return next;
    });
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setUploading(true);
    const blobUrl = URL.createObjectURL(file);
    try {
      const url = await firebaseLargeFileUploadHelper({
        blobUrl,
        storagePath: 'cashApplication/uploads',
        fileNameWithExtension: `${Date.now()}_${file.name}`,
      });

      if (!url) {
        openDillToast({ message: `Failed to upload ${file.name}.`, type: 'ERROR' });
        return;
      }

      setAttachment({
        url,
        name: file.name,
        fileType: file.type || 'application/octet-stream',
      });
    } finally {
      URL.revokeObjectURL(blobUrl);
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!supplierId || !canSave) {
      return;
    }

    setSaving(true);
    try {
      const result = await dispatch(
        createManualRemittanceThunk({
          supplierId,
          payerName: payerName.trim(),
          amount: parsedAmount,
          paymentDate: paymentDate || null,
          checkNumber: checkNumber.trim() || null,
          memo: memo.trim() || null,
          postingNote: postingNote.trim() || null,
          buyerId: customerId || null,
          allocations: Object.entries(allocations).map(([invoiceId, allocated]) => {
            // AllocationEditor keys allocations by invoice.id, same as the apply path does.
            const invoice = buyerInvoices.find((candidate) => candidate.id === invoiceId);
            return {
              invoiceId,
              docNumber: invoice?.docNumber ?? '',
              amount: allocated,
              invoiceAmount: invoice?.amount ?? null,
              openBalance: invoice?.balance ?? null,
            };
          }),
          sourceFile: attachment,
          ...(depositId ? { bankTransactionId: depositId } : {}),
        })
      );

      if (result.meta.requestStatus === 'fulfilled') {
        const { settled } = result.payload as { settled: boolean };
        openDillToast({
          message: settled
            ? 'Payment recorded and linked to the deposit. Approve it in 1-Click Review to post.'
            : 'Payment recorded.',
          type: 'SUCCESS',
        });
        onClose();
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to record the payment.',
          type: 'ERROR',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={deposit ? 'Record the payment for this deposit' : 'Record a payment'}
      subtitle={
        deposit
          ? `${formatCurrency(deposit.amount)} on ${formatDate(deposit.date)} — ${describeDeposit(
              deposit
            )}`
          : 'For advice that arrived outside your inbox, uploads or lockbox.'
      }
      maxWidth="820px"
      footer={
        <>
          <DillButton onClick={onClose} label="Cancel" variant="ghost" />
          <DillButton
            onClick={handleSave}
            label={saving ? 'Saving...' : depositId ? 'Record and link' : 'Record payment'}
            variant="primary"
            isLoading={saving}
            isDisabled={!canSave}
          />
        </>
      }>
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          <DillField label="Customer" className="flex-[2]">
            {/* Searchable: this is every customer the supplier has, which is hundreds for most of
                them, and scrolling to one by eye is the slowest part of recording a payment. */}
            <DillSelect
              value={customerId}
              onChange={handleCustomerChange}
              options={customerOptions}
              searchable
              searchPlaceholder="Search customers..."
              placeholder={
                loadingCustomers && customerOptions.length === 0
                  ? 'Loading customers...'
                  : 'Select a customer'
              }
            />
          </DillField>
          <DillField label="Payer name" required={true} className="flex-[2]">
            <DillInput
              value={payerName}
              onChange={setPayerName}
              placeholder="As it appears on the payment"
            />
          </DillField>
        </div>

        <div className="flex gap-3">
          <DillField label="Amount" required={true} className="flex-1">
            <DillInput
              value={amount}
              onChange={setAmount}
              placeholder="0.00"
              type="number"
              disabled={!!deposit}
            />
          </DillField>
          <DillField label="Payment date" className="flex-1">
            <DillInput
              value={paymentDate}
              onChange={setPaymentDate}
              type="date"
              disabled={!!deposit}
            />
          </DillField>
          <DillField label="Check number" className="flex-1">
            <DillInput value={checkNumber} onChange={setCheckNumber} placeholder="Optional" />
          </DillField>
        </div>

        {/*
          Two free-text boxes, so the placeholders have to carry the difference — "Optional" twice
          told the operator nothing. Memo transcribes the payment itself and is what the Exceptions
          queue searches on; Notes is the operator's own and is the one that reaches the ERP.
        */}
        <DillField label="Memo">
          <DillInput
            value={memo}
            onChange={setMemo}
            placeholder="Optional — the memo written on the payment itself"
          />
        </DillField>

        <DillField label="Notes">
          <textarea
            value={postingNote}
            onChange={(event) => setPostingNote(event.target.value)}
            rows={2}
            maxLength={ERP_POSTING_NOTE_MAX_LENGTH}
            placeholder="Optional — your own note about this payment"
            className="w-full px-3 py-2 text-sm text-gray-800 bg-white border border-gray-200 rounded-lg resize-y placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
          />
        </DillField>

        <DillField label="Bank deposit">
          {deposit ? (
            <div className="flex items-center gap-2 h-9 px-3 rounded-lg border border-blue-200 bg-blue-50">
              <span className="text-sm font-medium text-blue-900">
                {formatCurrency(deposit.amount)} · {formatDate(deposit.date)}
              </span>
              <span className="text-xs text-blue-700 truncate">{describeDeposit(deposit)}</span>
            </div>
          ) : (
            <DillSelect
              value={depositId}
              onChange={setDepositId}
              options={linkableDeposits.map((record) => ({
                value: record.id,
                label: `${formatCurrency(record.amount)} · ${formatDate(
                  record.date
                )} · ${describeDeposit(record)}`,
              }))}
              placeholder={
                linkableDeposits.length > 0
                  ? 'Optional — link a deposit that has already landed'
                  : 'No unlinked deposits in your feed'
              }
              disabled={linkableDeposits.length === 0}
            />
          )}
        </DillField>

        <DillField label="Attachment">
          {attachment ? (
            <div className="flex items-center justify-between gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white">
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 truncate">
                <File size={12} />
                {attachment.name}
              </a>
              <button
                onClick={() => setAttachment(null)}
                className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-1.5 h-9 px-3 self-start text-xs font-medium text-blue-600 border border-blue-200 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
              <Paperclip size={14} />
              {uploading ? 'Uploading...' : 'Attach a check image or remittance'}
              <input
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          )}
        </DillField>

        {customerId && loadingInvoices ? (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500">Loading this customer's open invoices...</p>
          </div>
        ) : customerId ? (
          <AllocationEditor
            invoices={customerInvoices}
            allocations={allocations}
            onAllocationChange={handleAllocationChange}
            totalAllocated={totalAllocated}
            paymentAmount={amountValid ? parsedAmount : 0}
            searchValue={invoiceSearch}
            onSearchChange={setInvoiceSearch}
            title="Allocate to invoices"
            searchPlaceholder="Search invoice #..."
          />
        ) : (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500">
              Select a customer to allocate this payment to their open invoices. You can also save
              it unallocated and apply it later.
            </p>
          </div>
        )}

        {overAllocated && (
          <p className="text-xs text-red-600">
            Allocated {formatCurrency(totalAllocated)} is more than the payment of{' '}
            {formatCurrency(parsedAmount)}.
          </p>
        )}

        {depositId && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-800">
              The deposit is confirmed money, so this payment is settled as soon as you save it.
              Posting is still a separate approval in 1-Click Review.
            </p>
          </div>
        )}
      </div>
    </DillModal>
  );
};
